import type { GlobalState, SelectedTab } from "./slices/GlobalSlice";
import type { TrainingLocalizationState } from "./slices/TrainingLocalizationSlice";
import type { TrainingState } from "./slices/TrainingSlice";
import type { ValidationState } from "./slices/ValidationSlice";
import { QUIZ_TYPES_WITH_FEEDBACK } from "../models/QuizFeedbackHelper";
import type Take from "../models/Take";
import {
  cloneDocumentSnapshot,
  extractDocumentSnapshot,
  restoreDocumentSnapshot,
  type DocumentSnapshot,
} from "./documentState";

export interface EditorFocusSnapshot {
  readonly selectedTake: string;
  readonly selectedElementId: string;
  readonly selectedElementIsTake: boolean;
  readonly selectedElementIsInteraction: boolean;
  readonly selectedSequenceTakeId: string;
  readonly selectedActor: string;
  readonly selectedProp: string;
  readonly localizationLanguageShortcode: string;
  readonly selectedTab: SelectedTab;
  readonly isPropertiesColumnOpen: boolean;
  readonly quizFeedback: {
    readonly isOpen: boolean;
    readonly parentQuizId: string;
  };
}

export interface HistoryFrame {
  readonly document: DocumentSnapshot;
  readonly focus: EditorFocusSnapshot;
  readonly validation: ValidationState;
}

export interface HistoryEntry {
  readonly before: HistoryFrame;
  readonly after: HistoryFrame;
}

export type HistoryRootState = {
  training: TrainingState;
  trainingLocalization: TrainingLocalizationState;
  global: GlobalState;
  validation: ValidationState;
};

function cloneValidationState(validation: ValidationState): ValidationState {
  return {
    warnings: Object.fromEntries(
      Object.entries(validation.warnings).map(([interactionId, warnings]) => [
        interactionId,
        [...warnings],
      ])
    ),
    errors: Object.fromEntries(
      Object.entries(validation.errors).map(([interactionId, errors]) => [
        interactionId,
        errors.map((error) => ({ ...error })),
      ])
    ),
  };
}

export function extractEditorFocusSnapshot(
  state: HistoryRootState
): EditorFocusSnapshot {
  return {
    selectedTake: state.training.selectedTake,
    selectedElementId: state.training.selectedElementId,
    selectedElementIsTake: state.training.selectedElementIsTake,
    selectedElementIsInteraction: state.training.selectedElementIsInteraction,
    selectedSequenceTakeId: state.training.selectedSequenceTakeId,
    selectedActor: state.training.selectedActor,
    selectedProp: state.training.selectedProp,
    localizationLanguageShortcode:
      state.trainingLocalization.currentLanguage?.shortcode ?? "",
    selectedTab: state.global.selectedTab,
    isPropertiesColumnOpen: state.global.isPropertiesColumnOpen,
    quizFeedback: {
      isOpen: state.global.quizFeedback?.isOpen ?? false,
      parentQuizId: state.global.quizFeedback?.parentQuizId ?? "",
    },
  };
}

export function createHistoryFrame(state: HistoryRootState): HistoryFrame {
  const focus = extractEditorFocusSnapshot(state);
  return {
    document: cloneDocumentSnapshot(extractDocumentSnapshot(state)),
    focus: { ...focus, quizFeedback: { ...focus.quizFeedback } },
    validation: cloneValidationState(state.validation),
  };
}

function containsId(value: unknown, id: string): boolean {
  if (!id || value === null || typeof value !== "object") return false;
  if ((value as { id?: unknown }).id === id) return true;
  if (Array.isArray(value)) return value.some((entry) => containsId(entry, id));
  return Object.values(value as Record<string, unknown>).some((entry) =>
    containsId(entry, id)
  );
}

function findOwningTake(takes: Take[], elementId: string): Take | undefined {
  return takes.find((take) => containsId(take, elementId));
}

function hasQuiz(takes: Take[], quizId: string): boolean {
  return takes.some((take) =>
    take.interactions.some(
      (interaction) =>
        interaction.id === quizId &&
        QUIZ_TYPES_WITH_FEEDBACK.has(interaction.type)
    )
  );
}

function resolveTab(selectedTab: SelectedTab, trainingType: number): SelectedTab {
  const isKnownTab =
    Number.isInteger(selectedTab) && selectedTab >= 0 && selectedTab <= 3;
  const isAvailableForDocument =
    trainingType === 0 ||
    (trainingType === 1 && selectedTab <= 2) ||
    selectedTab === 0;
  return isKnownTab && isAvailableForDocument
    ? selectedTab
    : (0 as SelectedTab);
}

export function restoreHistoryFrame<T extends HistoryRootState>(
  state: T,
  frame: HistoryFrame
): Pick<T, "training" | "trainingLocalization" | "global" | "validation"> {
  const restoredDocument = restoreDocumentSnapshot(state, frame.document);
  const training = restoredDocument.training;
  const focus = frame.focus;
  const takes: Take[] =
    training.trainingType === 1 ? training.takes360 : training.takes3D;
  const takeIds = new Set(takes.map((take) => take.id));
  const elementOwner = findOwningTake(takes, focus.selectedElementId);
  const fallbackTake = takeIds.has(focus.selectedTake)
    ? focus.selectedTake
    : takes[0]?.id ?? "";
  const selectedTake = elementOwner?.id ?? fallbackTake;
  const selectedElementId = elementOwner
    ? focus.selectedElementId
    : selectedTake;
  const selectedElementIsTake =
    selectedElementId !== "" && selectedElementId === selectedTake;
  const sequenceTake = training.takes3D.find(
    (take) => take.id === focus.selectedSequenceTakeId
  );
  const selectedSequenceTakeId =
    elementOwner &&
    sequenceTake?.id === elementOwner.id &&
    sequenceTake.sequence?.id === selectedElementId
      ? focus.selectedSequenceTakeId
      : "";
  const localizedLanguages =
    restoredDocument.trainingLocalization.localizedLanguages;
  const currentLanguage =
    localizedLanguages.find(
      (language) => language.shortcode === focus.localizationLanguageShortcode
    ) ??
    localizedLanguages.find(
      (language) => language.shortcode === training.referenceLanguage
    ) ??
    localizedLanguages[0] ?? { shortcode: "", full: "" };
  const selectedTab = resolveTab(focus.selectedTab, training.trainingType);
  const quizFeedbackIsValid =
    focus.quizFeedback.isOpen && hasQuiz(takes, focus.quizFeedback.parentQuizId);

  return {
    training: {
      ...training,
      selectedTake,
      selectedElementId,
      selectedElementIsTake,
      selectedElementIsInteraction: selectedElementIsTake
        ? false
        : elementOwner
        ? focus.selectedElementIsInteraction
        : false,
      selectedSequenceTakeId,
      selectedActor: training.actors.some(
        (actor) => actor.id === focus.selectedActor
      )
        ? focus.selectedActor
        : "",
      selectedProp: training.props.some((prop) => prop.id === focus.selectedProp)
        ? focus.selectedProp
        : "",
    },
    trainingLocalization: {
      ...restoredDocument.trainingLocalization,
      currentLanguage,
    },
    global: {
      ...state.global,
      selectedTab,
      isPropertiesColumnOpen: focus.isPropertiesColumnOpen,
      quizFeedback: quizFeedbackIsValid
        ? { ...focus.quizFeedback }
        : { isOpen: false, parentQuizId: "" },
    },
    validation: cloneValidationState(frame.validation),
  };
}
